import { useCallback, useState } from "react";

export interface BbWcToggleState {
  fallback: boolean;
  always: boolean;
  setFallback: (value: boolean) => void;
  setAlways: (value: boolean) => void;
}

/**
 * One "fallback" / "always" pair, coupled the way the backend contract
 * requires.
 *
 * "Always" strictly contains "fallback": an order the forwarder has not
 * invoiced yet has no invoiced figure to prefer either way, so under "always"
 * it still takes the quoted rate. Two free booleans would admit a fourth,
 * meaningless state - always on, fallback off - so the coupling lives here, in
 * the setters, rather than trusted to every caller of them:
 * - ticking "always" also ticks "fallback";
 * - unticking "fallback" also unticks "always".
 *
 * Used twice by {@link ./useBbWcRates.ts}: landing and customs are independent
 * pairs, so each gets its own instance.
 *
 * @example
 * const rate = useBbWcToggle();
 * rate.setAlways(true); // rate.fallback is now also true
 */
export function useBbWcToggle(): BbWcToggleState {
  const [fallback, setFallbackState] = useState(false);
  const [always, setAlwaysState] = useState(false);

  const setFallback = useCallback((value: boolean) => {
    setFallbackState(value);
    if (!value) setAlwaysState(false);
  }, []);

  const setAlways = useCallback((value: boolean) => {
    setAlwaysState(value);
    if (value) setFallbackState(true);
  }, []);

  return { fallback, always, setFallback, setAlways };
}
